import { forwardRef, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { format } from 'date-fns'
import { HugeiconsIcon } from '@hugeicons/react'
import { ArrowDown01Icon, Camera01Icon, Loading01Icon, Refresh01Icon } from '@hugeicons/core-free-icons'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { ScrollDepthSummary } from '@/lib/utils/heatmapUtils'
import type { HeatmapPoint } from './HeatmapOverlay'
import { HeatmapCapture, type HeatmapCaptureHandle } from './HeatmapCapture'
import { HeatmapTopBar, type HeatmapPageMenuActions, type HeatmapPageOption } from './HeatmapTopBar'
import { HeatmapView } from './HeatmapView'
import { HeatmapCollectingProgress } from './HeatmapStates'
import { HeatmapElementList } from './HeatmapElementList'
import {
  HEATMAP_READABLE_CLICKS,
  buildClicksAnswer,
  buildCollectingAnswer,
  buildHeatmapStats,
  buildScrollAnswer,
  collectingPace,
  elementInsight,
  experimentLink,
  foldDepthPercent,
  formatCount,
  formatPeriodRange,
  isHeatmapReadable,
  pagePath,
  rankHeatmapElements,
  totalClicksOf,
  type DeviceShares,
  type HeatmapDevice,
  type HeatmapMode,
} from './heatmapInsights'

export interface HeatmapPageBodyProps extends HeatmapPageMenuActions {
  pages: HeatmapPageOption[]
  selectedIndex: number
  onSelectPage: (index: number) => void
  mode: HeatmapMode
  onModeChange: (mode: HeatmapMode) => void
  device: HeatmapDevice
  onDeviceChange: (device: HeatmapDevice) => void
  deviceShares?: DeviceShares
  period: string
  onPeriodChange: (period: string) => void
  /** Click points for the selected device. */
  points: HeatmapPoint[]
  scrollDepth: ScrollDepthSummary
  screenshotUrl: string | null
  capturedAt: string | null
  /** Capture viewport width when known; otherwise the image's natural width. */
  captureWidth?: number
  /** Route prefix for experiment links ("/p/acme"); null hides test CTAs (share links). */
  basePath: string | null
  onTestClick?: (target: 'page' | 'element') => void
  notice?: ReactNode
  initialSelectedKey?: string | null
  initialIntensity?: number
  /** Fixed "now" for date labels (the DEV states gallery). */
  now?: Date
}

/**
 * Everything below the shell header on Heatmaps, as a pure view of props, so
 * the page and the DEV states gallery render the same thing. Picks the state
 * from the data: collecting (fewer than HEATMAP_READABLE_CLICKS clicks, shown
 * as dots), clicks heat, or scroll reach.
 */
export const HeatmapPageBody = forwardRef<HeatmapCaptureHandle, HeatmapPageBodyProps>(
  (
    {
      pages,
      selectedIndex,
      onSelectPage,
      mode,
      onModeChange,
      device,
      onDeviceChange,
      deviceShares,
      period,
      onPeriodChange,
      points,
      scrollDepth,
      screenshotUrl,
      capturedAt,
      captureWidth,
      basePath,
      onTestClick,
      notice,
      initialSelectedKey = null,
      initialIntensity = 0.8,
      now,
      ...menu
    },
    ref,
  ) => {
    const [selectedKey, setSelectedKey] = useState<string | null>(initialSelectedKey)
    const [intensity, setIntensity] = useState(initialIntensity)
    const [hoveredKey, setHoveredKey] = useState<string | null>(null)
    const [naturalSize, setNaturalSize] = useState<{ width: number; height: number } | null>(null)
    const naturalWidth = naturalSize?.width ?? null

    const page = pages[selectedIndex]
    const pageUrl = page?.page_url ?? ''
    const path = pagePath(pageUrl)

    // A selection belongs to one page, device and mode.
    const scopeKey = `${pageUrl}|${device}|${mode}`
    const firstScope = useRef(scopeKey)
    useEffect(() => {
      if (scopeKey !== firstScope.current) {
        setSelectedKey(null)
        setHoveredKey(null)
      }
    }, [scopeKey])

    const elements = useMemo(() => rankHeatmapElements(points), [points])
    const totalClicks = useMemo(() => totalClicksOf(points), [points])
    const rangeLabel = formatPeriodRange(period, now)
    const visitors = scrollDepth && scrollDepth.totalSessions > 0 ? scrollDepth.totalSessions : null
    const collecting = mode === 'clicks' && !isHeatmapReadable(totalClicks)
    const layer = mode === 'scroll' ? 'scroll' : collecting ? 'dots' : 'clicks'
    const top = elements[0]
    const foldDepth = naturalSize
      ? foldDepthPercent({ device, naturalWidth: naturalSize.width, naturalHeight: naturalSize.height, captureWidth })
      : null
    const stats = collecting ? [] : buildHeatmapStats({ device, elements, totalClicks, scroll: scrollDepth, foldDepth })

    const answer =
      mode === 'scroll'
        ? buildScrollAnswer({ path, scroll: scrollDepth, rangeLabel })
        : collecting
          ? buildCollectingAnswer({ path, device, totalClicks, visitors, rangeLabel })
          : buildClicksAnswer({ path, device, top, totalClicks, visitors, rangeLabel })

    const selectedElement = selectedKey && !collecting ? elements.find((e) => e.key === selectedKey) : undefined

    // Clicks heat carries no test CTA: "Test this button" read as unclear
    // (2026-10-15). The scroll answer keeps its page-level test.
    const cta =
      basePath && !selectedElement && !collecting && pageUrl && mode === 'scroll'
        ? { label: 'Test a shorter page', href: experimentLink(basePath, pageUrl), onClick: () => onTestClick?.('page') }
        : null

    // A capture older than the clicks it carries can put heat on elements
    // that have moved since; say so instead of silently mis-placing it.
    const captureAgeDays = capturedAt ? (Date.now() - new Date(capturedAt).getTime()) / 86_400_000 : null
    const staleCapture = captureAgeDays != null && captureAgeDays > STALE_CAPTURE_DAYS

    const width = captureWidth ?? naturalWidth
    // Another capture on the same day: add the time so switching between them
    // changes the label.
    const sameDayCaptures =
      capturedAt != null &&
      (menu.captures?.filter((c) => c.label.startsWith(format(new Date(capturedAt), 'MMM d, yyyy'))).length ?? 0) > 1
    const captureMeta = capturedAt
      ? `Captured ${format(new Date(capturedAt), new Date(capturedAt).getFullYear() === new Date().getFullYear() ? 'MMM d' : 'MMM d, yyyy')}${sameDayCaptures ? `, ${format(new Date(capturedAt), 'h:mm a')}` : ''} · ${device}${width ? `, ${width}px wide` : ''}`
      : `Not captured yet · ${device}`

    const unit = device === 'mobile' ? 'taps' : 'clicks'
    const dotsCaption =
      totalClicks === 0
        ? `No ${unit} yet. Heat shading turns on at about ${HEATMAP_READABLE_CLICKS}.`
        : `Showing ${points.length === totalClicks ? 'each of the' : 'the'} ${formatCount(totalClicks)} ${unit} as ${points.length === totalClicks ? 'a dot' : 'dots'}. Heat shading turns on at about ${HEATMAP_READABLE_CLICKS}.`

    return (
      <HeatmapView
        topBar={
          <HeatmapTopBar
            pages={pages}
            selectedIndex={selectedIndex}
            onSelectPage={onSelectPage}
            visitors={visitors}
            mode={mode}
            onModeChange={onModeChange}
            device={device}
            onDeviceChange={onDeviceChange}
            deviceShares={deviceShares}
            period={period}
            onPeriodChange={onPeriodChange}
            now={now}
            {...menu}
          />
        }
        answer={answer}
        stats={stats}
        cta={cta}
        answerAside={
          collecting ? (
            <HeatmapCollectingProgress
              totalClicks={totalClicks}
              device={device}
              pace={collectingPace({ totalClicks, period, device, now })}
            />
          ) : undefined
        }
        notice={
          notice || staleCapture ? (
            <div className="flex flex-col gap-2">
              {notice}
              {staleCapture && (
                <StaleCaptureNotice
                  capturedAt={capturedAt!}
                  onRecapture={menu.onRecapture}
                  isCapturing={menu.isCapturing}
                />
              )}
            </div>
          ) : undefined
        }
        aside={
          layer === 'clicks' && screenshotUrl ? (
            <HeatmapElementList
              elements={elements}
              device={device}
              selectedKey={selectedElement?.key ?? null}
              onSelect={setSelectedKey}
              onHover={setHoveredKey}
            />
          ) : undefined
        }
        layer={layer}
        device={device}
        captureMeta={
          <HeatmapCaptureMenu
            label={captureMeta}
            captures={menu.captures}
            selectedCapture={menu.selectedCapture}
            onSelectCapture={menu.onSelectCapture}
            onRecapture={screenshotUrl ? menu.onRecapture : undefined}
            isCapturing={menu.isCapturing}
          />
        }
        dotsCaption={dotsCaption}
        intensity={intensity}
        onIntensityChange={setIntensity}
        capture={
          screenshotUrl ? (
            <HeatmapCapture
              ref={ref}
              screenshotUrl={screenshotUrl}
              pageUrl={pageUrl}
              device={device}
              layer={layer}
              points={points}
              scrollDepth={scrollDepth}
              opacity={intensity}
              elements={elements}
              onSelectElement={setSelectedKey}
              hoveredKey={hoveredKey}
              foldDepth={foldDepth}
              onNaturalSize={setNaturalSize}
              selected={
                selectedElement
                  ? {
                      element: selectedElement,
                      totalElements: elements.length,
                      insight: elementInsight(selectedElement, elements, device),
                      device,
                    }
                  : null
              }
            />
          ) : (
            <NoCapture onRecapture={menu.onRecapture} isCapturing={menu.isCapturing} />
          )
        }
      />
    )
  },
)

HeatmapPageBody.displayName = 'HeatmapPageBody'

const STALE_CAPTURE_DAYS = 60

/**
 * The capture strip's meta line, made actionable: which capture the heat sits
 * on (a menu when there are earlier ones) and a visible Recapture button. These
 * lived at the bottom of the page picker, where nobody found them.
 */
function HeatmapCaptureMenu({
  label,
  captures,
  selectedCapture,
  onSelectCapture,
  onRecapture,
  isCapturing,
}: {
  label: string
  captures?: Array<{ value: string; label: string }>
  selectedCapture?: string
  onSelectCapture?: (value: string) => void
  onRecapture?: () => void
  isCapturing?: boolean
}) {
  const hasHistory = !!captures && captures.length > 1 && !!onSelectCapture
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {hasHistory ? (
        <DropdownMenu>
          <DropdownMenuTrigger
            aria-label="Choose capture"
            className="inline-flex h-7 items-center gap-1.5 rounded-lg px-2 text-[12px] leading-4 text-foreground/70 shadow-[0_0_0_1px_var(--input)] transition-colors hover:bg-black/[0.03] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 data-[state=open]:bg-black/[0.03] dark:hover:bg-white/5"
          >
            <HugeiconsIcon icon={Camera01Icon} size={13} className="shrink-0 text-foreground/45" />
            {label}
            <span className="text-foreground/45">· {captures.length} captures</span>
            <HugeiconsIcon icon={ArrowDown01Icon} size={12} className="shrink-0 text-foreground/45" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[260px]">
            <DropdownMenuLabel className="text-[12px] font-normal text-muted-foreground">
              Show heat on the capture from
            </DropdownMenuLabel>
            <DropdownMenuRadioGroup value={selectedCapture} onValueChange={onSelectCapture}>
              {captures.map((c) => (
                <DropdownMenuRadioItem key={c.value} value={c.value} className="text-[13px]">
                  {c.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
            {onRecapture && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={onRecapture} disabled={isCapturing} className="text-[13px]">
                  <HugeiconsIcon icon={Refresh01Icon} size={14} />
                  {isCapturing ? 'Capturing…' : 'Capture the page again'}
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <span className="text-[12px] leading-4 text-foreground/60">{label}</span>
      )}
      {onRecapture && (
        <button
          type="button"
          onClick={onRecapture}
          disabled={isCapturing}
          title="Take a fresh screenshot of this page"
          className="inline-flex h-7 items-center gap-1.5 rounded-lg px-2 text-[12px] font-medium leading-4 text-foreground/70 transition-colors hover:bg-black/[0.04] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-60 dark:hover:bg-white/5"
        >
          <HugeiconsIcon
            icon={isCapturing ? Loading01Icon : Refresh01Icon}
            size={13}
            className={isCapturing ? 'animate-spin motion-reduce:animate-none' : undefined}
          />
          {isCapturing ? 'Capturing…' : 'Recapture'}
        </button>
      )}
    </div>
  )
}

function StaleCaptureNotice({
  capturedAt,
  onRecapture,
  isCapturing,
}: {
  capturedAt: string
  onRecapture?: () => void
  isCapturing?: boolean
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-warning/20 bg-warning-tint px-3.5 py-2.5">
      <p className="min-w-0 flex-1 text-[13px] leading-[18px] text-foreground">
        This capture is from {format(new Date(capturedAt), 'MMM d, yyyy')}. If the page has changed since, clicks can land on the wrong spot.
      </p>
      {onRecapture && (
        <Button variant="outline" onClick={onRecapture} disabled={isCapturing} className="h-8 shrink-0 gap-1.5 text-[13px]">
          <HugeiconsIcon icon={isCapturing ? Loading01Icon : Refresh01Icon} size={14} className={isCapturing ? 'animate-spin' : undefined} />
          {isCapturing ? 'Capturing…' : 'Recapture page'}
        </Button>
      )}
    </div>
  )
}

function NoCapture({ onRecapture, isCapturing }: { onRecapture?: () => void; isCapturing?: boolean }) {
  return (
    <div className="flex w-full max-w-[800px] flex-col items-center gap-2 rounded-xl bg-card px-6 py-16 text-center shadow-[0_0_0_1px_rgb(9_9_11/0.05)]">
      <HugeiconsIcon icon={Camera01Icon} size={18} className="text-muted-foreground/70" />
      <p className="text-sm font-medium text-ui-title">No capture of this page yet</p>
      <p className="text-[13px] text-muted-foreground">Capture it to see clicks and scroll on top of the page.</p>
      {onRecapture && (
        <Button variant="outline" onClick={onRecapture} disabled={isCapturing} className="mt-2 h-8 gap-1.5 text-[13px]">
          <HugeiconsIcon icon={isCapturing ? Loading01Icon : Refresh01Icon} size={14} className={isCapturing ? 'animate-spin' : undefined} />
          {isCapturing ? 'Capturing…' : 'Capture page'}
        </Button>
      )}
    </div>
  )
}
